import { useEffect, useRef, useState } from 'react';
import { useAuth as useClerkAuth, useUser } from '@clerk/react';
import { UserProfile } from '../types';
import { initAnalytics, identifyUser, trackEvent } from '../services/analytics';
import { hasSeenOnboarding } from '../components/OnboardingTooltip';
import { api, ApiError } from '../services/api';
import { getStagerySessionClaims } from '../src/auth/getStagerySessionClaims';
import { resolveProfileFormDefaults } from '../src/profile/prefillFromClerk';

declare global {
  interface Window {
    aistudio?: { hasSelectedApiKey: () => Promise<boolean>; openSelectKey: () => Promise<void> };
  }
}

const BYPASS = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_DEV_BYPASS_AUTH === '1';

/**
 * STA-149 — Fetch the app profile from `/api/auth/me`.
 *
 * On a user's first authenticated request, stagery-api provisions their DB
 * row just-in-time (JIT). That provisioning can fail transiently with a 5xx
 * — notably a unique partial-index conflict when the same Clerk user is
 * provisioned by two concurrent requests — which would otherwise bounce a
 * freshly signed-up user straight to the "profiel niet laden" screen and
 * lock them out.
 *
 * Retry a bounded number of times with backoff so a transient provisioning
 * failure self-heals: by the time we retry, the racing request has usually
 * committed the row, so `/api/auth/me` finds it and returns 200. A 4xx
 * (incl. 401) is treated as terminal and surfaces immediately — it is not
 * transient and retrying would only delay the error UI.
 *
 * The durable fix is an idempotent upsert in stagery-api — see STA-149 and
 * /tmp/sta149_api_fix.md.
 */
async function fetchAuthProfile(maxRetries = 2): Promise<Record<string, unknown>> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await api.get<Record<string, unknown>>('/api/auth/me');
    } catch (err) {
      // Non-ApiError (network failure) → status 0, also treated as transient.
      const status = err instanceof ApiError ? err.status : 0;
      const isTransient = status === 0 || status >= 500;
      if (!isTransient || attempt >= maxRetries) throw err;
      await new Promise((resolve) => setTimeout(resolve, 600 * Math.pow(2, attempt)));
    }
  }
}

function useAuthReal() {
  const { signOut, isLoaded: authLoaded, orgId: clerkOrgId, orgRole, actor: claims, getToken } = useClerkAuth();
  const { user: clerkUser, isLoaded: userLoaded } = useUser();
  // STA-149: key the profile-load effect on the stable Clerk user id, not the
  // `clerkUser` object — its reference churns on every Clerk re-render, which
  // re-fired `/api/auth/me` repeatedly and concurrently, racing JIT
  // provisioning in stagery-api.
  const clerkUserId = clerkUser?.id ?? null;

  const [user, setUser] = useState<UserProfile | null>(null);
  // True until Clerk + profile have both resolved. Starts true so the app never
  // flashes LoginScreen before we know whether a Clerk session exists.
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  /** Clerk session OK but `/api/auth/me` failed — avoids redirect loop with `/sign-in`. */
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);
  const [hasKey, setHasKey] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return !('aistudio' in window);
  });
  // STA-153, STA-168: initialize from localStorage so route remounts (each
  // path renders a separate <App /> in the router config) never transiently
  // show the modal for users who already dismissed it.
  const [showOnboarding, setShowOnboarding] = useState<boolean>(
    () => typeof window !== 'undefined' && !hasSeenOnboarding(),
  );

  // kantoor_id from the stagery_session JWT template. null until loaded.
  const [kantoorId, setKantoorId] = useState<number | null>(null);
  const [kantoorLoaded, setKantoorLoaded] = useState(false);
  const kantoorPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch kantoor_id from the custom JWT session claims. Polls every 3 s for
  // up to 30 s so the provisioning banner disappears as soon as the Clerk
  // webhook sets kantoor_id in publicMetadata (typically within 1-2 s).
  useEffect(() => {
    if (!authLoaded) return;

    if (!clerkUser) {
      setKantoorId(null);
      setKantoorLoaded(true);
      if (kantoorPollRef.current) clearInterval(kantoorPollRef.current);
      return;
    }

    let attempts = 0;
    const maxAttempts = 10; // 10 × 3 s = 30 s ceiling

    const check = async () => {
      try {
        const sessionClaims = await getStagerySessionClaims(getToken);
        const id = sessionClaims?.kantoor_id ?? null;
        setKantoorId(id);
        setKantoorLoaded(true);
        if (id !== null && kantoorPollRef.current) {
          clearInterval(kantoorPollRef.current);
          kantoorPollRef.current = null;
        }
      } catch {
        setKantoorLoaded(true);
      }
      attempts += 1;
      if (attempts >= maxAttempts && kantoorPollRef.current) {
        clearInterval(kantoorPollRef.current);
        kantoorPollRef.current = null;
      }
    };

    check();
    kantoorPollRef.current = setInterval(check, 3000);

    return () => {
      if (kantoorPollRef.current) clearInterval(kantoorPollRef.current);
    };
  }, [authLoaded, clerkUser, getToken]);

  useEffect(() => {
    if (!authLoaded || !userLoaded) return;
    initAnalytics();

    // STA-149: ignore a stale in-flight `/api/auth/me` if this effect re-runs
    // or unmounts before the request resolves, so a late response cannot
    // overwrite fresh state.
    let cancelled = false;

    if (clerkUserId) {
      setIsLoadingProfile(true);
      setProfileLoadFailed(false);
      fetchAuthProfile()
        .then(u => {
          if (cancelled) return;
          // DUTCH-14: when DB profile fields are empty, fill from Clerk so
          // greeting/topbar/profiel are not blank for users who already set
          // names (or company metadata) in Clerk.
          const fromClerk = resolveProfileFormDefaults({
            appFirstName: u.firstName as string | null | undefined,
            appLastName: u.lastName as string | null | undefined,
            appCompanyName: u.companyName as string | null | undefined,
            clerkFirstName: clerkUser?.firstName,
            clerkLastName: clerkUser?.lastName,
            clerkUnsafeMetadata: clerkUser?.unsafeMetadata as
              | Record<string, unknown>
              | undefined,
            clerkPublicMetadata: clerkUser?.publicMetadata as
              | Record<string, unknown>
              | undefined,
          });
          const profile: UserProfile = {
            firstName: fromClerk.firstName,
            lastName: fromClerk.lastName,
            companyName: fromClerk.companyName,
            email: u.email as string,
            credits: (u.credits as number) ?? 25,
            referralCode: (u.referralCode as string) || '',
            role: ((u.role as string) || 'beheerder') as 'beheerder' | 'medewerker' | 'superadmin',
            avatarUrl: u.avatarUrl as string | undefined,
            companyLogoUrl: u.companyLogoUrl as string | undefined,
            websiteUrl: u.websiteUrl as string | undefined,
            abVariant: (u.abVariant as string) || null,
            abExperiment: (u.abExperiment as string) || null,
            trialActive: (u.trialActive as boolean) || false,
            trialEndsAt: (u.trialEndsAt as string) || null,
            utmSource: (u.utmSource as string) || null,
            utmMedium: (u.utmMedium as string) || null,
            utmCampaign: (u.utmCampaign as string) || null,
            utmContent: (u.utmContent as string) || null,
          };
          setUser(profile);
          setProfileLoadFailed(false);
          identifyUser(u.email as string, profile);
          if (!hasSeenOnboarding()) setShowOnboarding(true);
        })
        .catch(() => {
          if (cancelled) return;
          setUser(null);
          setProfileLoadFailed(true);
        })
        .finally(() => {
          if (cancelled) return;
          setIsLoadingProfile(false);
        });
    } else {
      setUser(null);
      setProfileLoadFailed(false);
      setIsLoadingProfile(false);
    }

    const checkKey = async () => {
      if (window.aistudio) {
        const hasSelected = await window.aistudio.hasSelectedApiKey();
        if (!cancelled) setHasKey(hasSelected);
      } else if (!cancelled) {
        setHasKey(true);
      }
    };
    checkKey();

    return () => {
      cancelled = true;
    };
  }, [authLoaded, userLoaded, clerkUserId]);

  const handleLogout = () => {
    try {
      localStorage.removeItem('estateflow_jwt');
    } catch {
      // ignore (e.g. private mode)
    }
    trackEvent('user_logout');
    void signOut().finally(() => {
      window.location.replace('/sign-in');
    });
  };

  const handleConnectKey = async () => {
    if (window.aistudio) {
      try {
        await window.aistudio.openSelectKey();
        setHasKey(true);
      } catch (e) {
        console.error("Key selection failed", e);
      }
    }
  };

  return {
    user, setUser,
    hasKey, setHasKey,
    showOnboarding, setShowOnboarding,
    isLoadingProfile,
    profileLoadFailed,
    authLoaded,
    userLoaded,
    orgLoaded: kantoorLoaded,
    orgId: kantoorId,
    // P1-03: expose Clerk's native orgId so the provisioning banner can also
    // suppress itself the moment Clerk reports an org — without waiting for the
    // kantoor_id JWT claim to propagate (which may lag the Clerk webhook by
    // several seconds and cause the banner to linger at mobile widths).
    clerkOrgId,
    orgRole,
    orgPermissions: [] as string[],
    claims,
    handleLogout, handleConnectKey,
  };
}

function useAuthBypass() {
  const [user, setUser] = useState<UserProfile | null>({
    firstName: 'Demo',
    lastName: 'Makelaar',
    companyName: 'Stagery Demo',
    email: 'demo@stagery.local',
    credits: 142,
    referralCode: 'DEMO',
    role: 'beheerder',
    avatarUrl: undefined,
    companyLogoUrl: undefined,
    websiteUrl: undefined,
    abVariant: null,
    abExperiment: null,
    trialActive: false,
    trialEndsAt: null,
    utmSource: null,
    utmMedium: null,
    utmCampaign: null,
    utmContent: null,
  });
  const [hasKey, setHasKey] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const handleLogout = () => setUser(null);
  const handleConnectKey = async () => setHasKey(true);

  return {
    user, setUser,
    hasKey, setHasKey,
    showOnboarding, setShowOnboarding,
    isLoadingProfile: false,
    profileLoadFailed: false,
    authLoaded: true,
    userLoaded: true,
    orgLoaded: false,
    orgId: undefined,
    clerkOrgId: undefined,
    orgRole: undefined,
    orgPermissions: [] as string[],
    claims: undefined,
    handleLogout, handleConnectKey,
  };
}

export const useAuth = BYPASS ? useAuthBypass : useAuthReal;
